'use client';

import {
	CountrySchema,
	type FileToUpload,
	type StepperRef,
	uploadFiles,
	useAppLoader,
	useCountries,
	useIntl,
	type UserProofOfAddressManualRequirementConfig,
	type UserProvidedValues,
	type UserRequirementStateStatus
} from '@capsicum/common';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { PROOF_OF_ADDRESS_UPLOAD_PROPS } from '../constants';
import { useSubmitRequirementStateByIdentifierQuery } from '../data-access';

import type { Requirement } from '@capsicum/socios-core';

type ProofOfAddressFormType = z.infer<ReturnType<typeof useProofOfAddressSchema>>;

const useProofOfAddressSchema = () => {
	const { $t } = useIntl();

	return useMemo(
		() =>
			z.object({
				city: z
					.string()
					.min(
						2,
						$t('requirements.common.form.errors.length', {
							field: $t('requirements.common.form.city.label'),
							max: 255,
							min: 2,
							value: $t('requirements.common.form.errors.short')
						})
					)
					.max(
						255,
						$t('requirements.common.form.errors.length', {
							field: $t('requirements.common.form.city.label'),
							max: 255,
							min: 2,
							value: $t('requirements.common.form.errors.long')
						})
					),
				country: CountrySchema,
				region: z.union([
					z.string().refine(val => val.length === 0 || (val.length >= 2 && val.length <= 255), {
						message: $t('requirements.common.form.errors.length', {
							field: $t('requirements.common.form.region.labelShort'),
							max: 255,
							min: 2,
							value: $t('requirements.common.form.errors.long')
						})
					}),
					z.null()
				]),
				street: z
					.string()
					.min(
						2,
						$t('requirements.common.form.errors.length', {
							field: $t('requirements.common.form.street.label'),
							max: 512,
							min: 2,
							value: $t('requirements.common.form.errors.short')
						})
					)
					.max(
						512,
						$t('requirements.common.form.errors.length', {
							field: $t('requirements.common.form.street.label'),
							max: 512,
							min: 2,
							value: $t('requirements.common.form.errors.long')
						})
					),
				zipCode: z
					.string()
					.min(
						2,
						$t('requirements.common.form.errors.length', {
							field: $t('requirements.common.form.postcode.label'),
							max: 50,
							min: 2,
							value: $t('requirements.common.form.errors.short')
						})
					)
					.max(
						50,
						$t('requirements.common.form.errors.length', {
							field: $t('requirements.common.form.postcode.label'),
							max: 50,
							min: 2,
							value: $t('requirements.common.form.errors.long')
						})
					)
			}),
		[]
	);
};

export const useProofOfAddress = ({
	requirement,
	status
}: {
	requirement: Requirement<UserProofOfAddressManualRequirementConfig>;
	status?: UserRequirementStateStatus;
}) => {
	const { $t } = useIntl();
	const { countriesQuery, getCountryBy } = useCountries();
	const appLoader = useAppLoader();
	const stepperRef = useRef<StepperRef>(null);
	const [index, setIndex] = useState(0);
	const [uploadedFiles, setUploadedFiles] = useState<Array<FileToUpload>>([]);

	const defaultValues = {
		city: requirement.config.prefill.city || '',
		country: getCountryBy(requirement.config.prefill.countryCode, 'iso2'),
		region: requirement.config.prefill.region || '',
		street: requirement.config.prefill.street || '',
		zipCode: requirement.config.prefill.zipCode || ''
	};

	const proofOfAddressForm = useForm<ProofOfAddressFormType>({
		defaultValues,
		delayError: 200,
		mode: 'onChange',
		resolver: zodResolver(useProofOfAddressSchema())
	});

	const isReadonlyMode = status === 'completed_allow';
	const firstButtonText = () => $t(`global.button.${index === 1 ? 'submit' : 'verify'}`);
	const isAlertVisible =
		uploadedFiles.length > PROOF_OF_ADDRESS_UPLOAD_PROPS.selectionLimit ||
		uploadedFiles.some(file => file.size && file.size > PROOF_OF_ADDRESS_UPLOAD_PROPS.maxSize);

	const disabledFirstButton = useMemo(() => {
		switch (index) {
			case 0:
				return !proofOfAddressForm.formState.isValid;
			case 1:
				return uploadedFiles.length === 0 || isAlertVisible;
			default:
				return false;
		}
	}, [index, proofOfAddressForm.formState.isValid, isAlertVisible, uploadedFiles]);

	useEffect(() => {
		if (countriesQuery.isFetched) {
			proofOfAddressForm.reset(defaultValues);
		}
	}, [countriesQuery.isFetched, countriesQuery.isFetching]);

	const {
		isLoading: isRequirementSubmissionLoading,
		isSuccess: requirementSubmissionIsSuccess,
		mutate: submitRequirement
	} = useSubmitRequirementStateByIdentifierQuery();

	const getDocumentsContent = async () => {
		const documents = uploadedFiles.map(file => ({
			image: file
		}));

		const result = await uploadFiles(documents);

		return result.map(file => file.requestId);
	};

	const submitProofOfAddress = async () => {
		const formValues = proofOfAddressForm.getValues();
		const documents = await getDocumentsContent();

		const requirementData: UserProvidedValues = {
			city: formValues.city,
			countryCode: formValues.country.iso2,
			documentsContentV2: documents,
			region: formValues.region || '',
			street: formValues.street,
			zipCode: formValues.zipCode
		};

		return submitRequirement({
			requirementData,
			requirementIdentifier: requirement.identifier
		});
	};

	const submit = async () => appLoader(submitProofOfAddress());

	return {
		disabledFirstButton,
		firstButtonText,
		index,
		isAlertVisible,
		isReadonlyMode,
		isRequirementSubmissionLoading,
		proofOfAddressForm,
		requirementSubmissionIsSuccess,
		setIndex,
		setUploadedFiles,
		stepperRef,
		submit
	};
};
